import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Eye,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  DollarSign,
  CheckCircle,
  AlertCircle,
  MessageCircle,
  Building2,
} from "lucide-react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { useAuth } from "../../context/AuthContext";

const BillsList = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Tracks which payment row is currently running a manual invoice generation
  const [generatingInvoiceId, setGeneratingInvoiceId] = useState(null);

  // ✅ diagnostic centers dropdown list
  const [diagnosticCenters, setDiagnosticCenters] = useState([]);
  const [isLoadingCenters, setIsLoadingCenters] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    billId: "",
    orderId: "", // ✅ exact order id (the "Order: 2747" shown in the table)
    paymentMethod: "",
    diagnosticCenterId: "", // ✅ NEW (instead of centerId)
    startDate: "",
    endDate: "",
    page: 1,
    limit: 10,
  });

  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    currentPage: 1,
    limit: 10,
  });

  const [stats, setStats] = useState({
    totalAmount: 0,
    totalRefunds: 0,
    netAmount: 0,
    totalPayments: 0,
  });

  // UI date state (DD-MM-YYYY)
  const [dateUI, setDateUI] = useState({ start: "", end: "" });
  const [dateErrors, setDateErrors] = useState({ start: "", end: "" });
  const didInitDefaults = useRef(false);

  const getTodayISOAndUI = () => {
    const today = new Date();
    const yyyy = String(today.getFullYear());
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return { iso: `${yyyy}-${mm}-${dd}`, ui: `${dd}-${mm}-${yyyy}` };
  };

  const toISODateFromDDMMYYYY = (ddmmyyyy) => {
    try {
      if (!ddmmyyyy) return "";
      const cleaned = String(ddmmyyyy).replace(/[^\d]/g, "");
      if (cleaned.length !== 8) return "";

      const dd = cleaned.slice(0, 2);
      const mm = cleaned.slice(2, 4);
      const yyyy = cleaned.slice(4, 8);

      const d = Number(dd);
      const m = Number(mm);
      const y = Number(yyyy);

      if (y < 1900 || y > 2100) return "";
      if (m < 1 || m > 12) return "";
      if (d < 1 || d > 31) return "";

      const dt = new Date(`${yyyy}-${mm}-${dd}T00:00:00.000Z`);
      if (
        dt.getUTCFullYear() !== y ||
        dt.getUTCMonth() + 1 !== m ||
        dt.getUTCDate() !== d
      )
        return "";

      return `${yyyy}-${mm}-${dd}`;
    } catch (e) {
      return "";
    }
  };

  const formatDDMMYYYYInput = (value) => {
    try {
      const digits = String(value || "")
        .replace(/[^\d]/g, "")
        .slice(0, 8);
      const dd = digits.slice(0, 2);
      const mm = digits.slice(2, 4);
      const yyyy = digits.slice(4, 8);
      let out = dd;
      if (mm) out += `-${mm}`;
      if (yyyy) out += `-${yyyy}`;
      return out;
    } catch (e) {
      return "";
    }
  };

  // ✅ Build dropdown options based on role
  const userDiagnosticCenters = useMemo(() => {
    try {
      // ask your auth structure:
      // user.assignedDiagnosticCenters: [{ diagnosticCenterId, diagnosticCenter: { id, name, status } }]
      const list = Array.isArray(user?.assignedDiagnosticCenters)
        ? user.assignedDiagnosticCenters
        : [];

      return list
        .filter((x) => x?.diagnosticCenter?.status === "active" || x?.diagnosticCenter?.status === undefined)
        .map((x) => ({
          id: x?.diagnosticCenterId ?? x?.diagnosticCenter?.id,
          name: x?.diagnosticCenter?.name || "Unknown Diagnostic Center",
        }))
        .filter((x) => x.id);
    } catch (e) {
      console.error(e);
      return [];
    }
  }, [user]);

  // ✅ fetch diagnostic centers (superadmin gets all)
  const fetchDiagnosticCenters = async () => {
    try {
      setIsLoadingCenters(true);

      // If superadmin, fetch all from API
      if (user?.role === "superadmin") {
        const res = await axiosInstance.get(`${API_BASE_URL}/api/diagnostic-centers`, {
          withCredentials: true,
        });
        setDiagnosticCenters(res.data?.data || []);
      } else {
        // non-superadmin: use assigned list only
        setDiagnosticCenters(userDiagnosticCenters);
      }
    } catch (err) {
      console.error("Error fetching diagnostic centers:", err);
      setDiagnosticCenters(userDiagnosticCenters);
    } finally {
      setIsLoadingCenters(false);
    }
  };

  const fetchBills = async (override = {}) => {
    try {
      setIsLoading(true);
      setError(null);

      const finalFilters = { ...filters, ...override };

      // enforce TODAY if empty
      const { iso: todayISO } = getTodayISOAndUI();
      if (!finalFilters.startDate) finalFilters.startDate = todayISO;
      if (!finalFilters.endDate) finalFilters.endDate = todayISO;

      const params = new URLSearchParams();
      Object.keys(finalFilters).forEach((key) => {
        const val = finalFilters[key];
        if (val !== "" && val !== null && val !== undefined) params.append(key, val);
      });

      const response = await axiosInstance.get(
        `${API_BASE_URL}/api/payments?${params.toString()}`,
        { withCredentials: true }
      );

      if (response.data?.success) {
        setBills(response.data.payments || []);

        setPagination({
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0,
          currentPage: response.data.pagination?.page || 1,
          limit: response.data.pagination?.limit || finalFilters.limit,
        });

        const summary = response.data.summary || {};
        setStats({
          totalAmount: summary.totalAmount || 0,
          totalRefunds: summary.totalRefunds || 0,
          netAmount: summary.netAmount || 0,
          totalPayments: summary.totalPayments || 0,
        });
      } else {
        setError(response.data?.message || "Failed to fetch bills");
      }
    } catch (err) {
      console.error("Error fetching bills:", err);
      setError(err.response?.data?.message || "Failed to load bills");
    } finally {
      setIsLoading(false);
    }
  };

  // INIT: TODAY + fetch centers + today payments
  useEffect(() => {
    try {
      const { iso, ui } = getTodayISOAndUI();

      setFilters((prev) => ({
        ...prev,
        startDate: iso,
        endDate: iso,
        page: 1,
      }));

      setDateUI({ start: ui, end: ui });
      setDateErrors({ start: "", end: "" });

      didInitDefaults.current = true;

      fetchDiagnosticCenters();
      fetchBills({ startDate: iso, endDate: iso, page: 1 });
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch on page/limit change after init
  useEffect(() => {
    try {
      if (!didInitDefaults.current) return;
      fetchBills();
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit]);

  const handleFilterChange = (e) => {
    try {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = (e) => {
    try {
      if (e?.preventDefault) e.preventDefault();

      if (dateErrors.start || dateErrors.end) {
        alert("Please fix date errors before applying.");
        return;
      }

      if (filters.startDate && filters.endDate) {
        const s = new Date(filters.startDate);
        const en = new Date(filters.endDate);
        if (s > en) {
          alert("From date cannot be greater than To date");
          return;
        }
      }

      fetchBills({ page: 1 });
    } catch (err) {
      console.error(err);
    }
  };

  const handleResetFilters = () => {
    try {
      const { iso, ui } = getTodayISOAndUI();

      setFilters({
        search: "",
        billId: "",
        orderId: "",
        paymentMethod: "",
        diagnosticCenterId: "", // ✅ reset
        startDate: iso,
        endDate: iso,
        page: 1,
        limit: 10,
      });

      setDateUI({ start: ui, end: ui });
      setDateErrors({ start: "", end: "" });

      fetchBills({ startDate: iso, endDate: iso, page: 1, limit: 10 });
    } catch (err) {
      console.error(err);
    }
  };

  const handlePageChange = (newPage) => {
    try {
      if (newPage >= 1 && newPage <= pagination.pages) {
        setFilters((prev) => ({ ...prev, page: newPage }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onDateUIChange = (key, value) => {
    try {
      const v = formatDDMMYYYYInput(value);
      setDateUI((p) => ({ ...p, [key]: v }));

      const digits = String(v).replace(/[^\d]/g, "");
      const iso = toISODateFromDDMMYYYY(v);

      setDateErrors((p) => ({
        ...p,
        [key]: digits.length === 8 && !iso ? "Invalid date (DD-MM-YYYY)" : "",
      }));

      setFilters((prev) => ({
        ...prev,
        [key === "start" ? "startDate" : "endDate"]: iso || "",
        page: 1,
      }));
    } catch (e) {
      console.error(e);
    }
  };

  // ✅ Calendar picker change (native <input type="date"> emits YYYY-MM-DD)
  const onDatePickChange = (key, isoValue) => {
    try {
      const ui = isoValue
        ? `${isoValue.slice(8, 10)}-${isoValue.slice(5, 7)}-${isoValue.slice(0, 4)}`
        : "";
      setDateUI((p) => ({ ...p, [key]: ui }));
      setDateErrors((p) => ({ ...p, [key]: "" }));
      setFilters((prev) => ({
        ...prev,
        [key === "start" ? "startDate" : "endDate"]: isoValue || "",
        page: 1,
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const setTodayRange = () => {
    try {
      const { iso, ui } = getTodayISOAndUI();
      setFilters((p) => ({ ...p, startDate: iso, endDate: iso, page: 1 }));
      setDateUI({ start: ui, end: ui });
      setDateErrors({ start: "", end: "" });
      fetchBills({ startDate: iso, endDate: iso, page: 1 });
    } catch (e) {
      console.error(e);
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return "—";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return "—";
    }
  };

  const formatDateTime = (dateString) => {
    try {
      if (!dateString) return "—";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return "—";
    }
  };

  const formatCurrency = (amount) => {
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        currencyDisplay: "symbol",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(amount || 0));
    } catch (e) {
      return `₹${amount || 0}`;
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (String(status || "").toUpperCase()) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "AUTHORIZED":
        return "bg-blue-100 text-blue-800";
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "FAILED":
        return "bg-red-100 text-red-800";
      case "REFUNDED":
        return "bg-purple-100 text-purple-800";
      case "CANCELLED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPaymentStatusText = (status) => {
    try {
      if (!status) return "—";
      return String(status)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    } catch (e) {
      return "—";
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (String(method || "").toUpperCase()) {
      case "CASH":
        return "₹";
      case "CARD":
        return "💳";
      case "UPI":
        return "📱";
      case "NETBANKING":
        return "🌐";
      case "WALLET":
        return "👛";
      case "CHEQUE":
        return "🧾";
      default:
        return "💰";
    }
  };

  const getSerialNumber = (index) => (filters.page - 1) * filters.limit + index + 1;

  const pageButtons = useMemo(() => {
    try {
      const total = pagination.pages || 0;
      const current = filters.page || 1;

      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

      const pages = new Set([1, total, current - 2, current - 1, current, current + 1, current + 2]);
      const filtered = [...pages]
        .filter((p) => p >= 1 && p <= total)
        .sort((a, b) => a - b);

      const result = [];
      for (let i = 0; i < filtered.length; i++) {
        const p = filtered[i];
        const prev = filtered[i - 1];
        if (i > 0 && p - prev > 1) result.push("...");
        result.push(p);
      }
      return result;
    } catch (e) {
      return [];
    }
  }, [pagination.pages, filters.page]);

  const sendInvoiceWhatsApp = (bill) => {
    try {
      const phone = bill?.patient?.contactNo ? String(bill.patient.contactNo).replace(/\D/g, "") : "";
      if (!phone || phone.length < 10) {
        alert("Patient mobile number not available to send WhatsApp.");
        return;
      }

      const orderNumber = bill?.order?.orderNumber || bill?.order?.id || "—";
      const centerName =
        bill?.diagnosticCenter?.name || bill?.center?.name || "Novus Health Labs";
      const amount = formatCurrency(bill?.amount || 0);
      const invoiceUrl = bill?.invoiceUrl || "";

      const msg =
        `Hello ${bill?.patient?.fullName || "Patient"},\n\n` +
        `Payment successful.\n` +
        `Center: ${centerName}\n` +
        `Order: ${orderNumber}\n` +
        `Amount: ${amount}\n\n` +
        (invoiceUrl ? `Invoice: ${invoiceUrl}\n\n` : "") +
        `Thank you.`;

      const url = `https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error(e);
      alert("Failed to open WhatsApp. Please try again.");
    }
  };

  // Manually (re)generate the invoice PDF for a payment whose background
  // job failed or was skipped. Hits POST /api/payments/:id/generate-invoice
  // and refreshes the list so the "Invoice" download button appears.
  const generateInvoiceManually = async (bill) => {
    if (!bill?.id) return;
    if (generatingInvoiceId) return; // prevent concurrent clicks
    try {
      setGeneratingInvoiceId(bill.id);
      const res = await axiosInstance.post(
        `${API_BASE_URL}/api/payments/${bill.id}/generate-invoice`,
        {},
        { withCredentials: true }
      );
      if (res.data?.success) {
        // Optimistically patch the row so the download link shows immediately,
        // then refetch to pick up any other server-side changes.
        setBills((prev) =>
          prev.map((b) =>
            b.id === bill.id
              ? {
                  ...b,
                  invoiceUrl: res.data.invoiceUrl || b.invoiceUrl,
                  invoiceNumber: res.data.invoiceNumber || b.invoiceNumber,
                }
              : b
          )
        );
        fetchBills();
      } else {
        alert(res.data?.message || "Failed to generate invoice");
      }
    } catch (err) {
      console.error("generateInvoiceManually error:", err);
      alert(
        err.response?.data?.message ||
          "Failed to generate invoice. Please try again."
      );
    } finally {
      setGeneratingInvoiceId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} isMenuOpen={sidebarOpen} />
     <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main className={`pt-14 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <div className="p-4">
          <Breadcrumb className="mb-4" />

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Total Payments</p>
                  <p className="text-base font-semibold text-gray-900">{pagination.total}</p>
                </div>
                <div className="p-1.5 bg-blue-50 rounded">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Total Amount</p>
                  <p className="text-base font-semibold text-gray-900">{formatCurrency(stats.totalAmount)}</p>
                </div>
                <div className="p-1.5 bg-green-50 rounded">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Net Amount</p>
                  <p className="text-base font-semibold text-green-600">{formatCurrency(stats.netAmount)}</p>
                </div>
                <div className="p-1.5 bg-green-50 rounded">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Total Refunds</p>
                  <p className="text-base font-semibold text-red-600">{formatCurrency(stats.totalRefunds)}</p>
                </div>
                <div className="p-1.5 bg-red-50 rounded">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-900">Filters</h3>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={setTodayRange}
                  className="px-2.5 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
                >
                  Today
                </button>
                <button
                  onClick={handleResetFilters}
                  className="px-2.5 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
                >
                  Reset
                </button>
                <button
                  onClick={handleSearch}
                  className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                  disabled={isLoading}
                >
                  {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                  Apply
                </button>
              </div>
            </div>

            <form onSubmit={handleSearch}>
              <div className="grid grid-cols-12 gap-3 text-xs">
                {/* Search */}
                <div className="col-span-12 md:col-span-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="text"
                      name="search"
                      value={filters.search}
                      onChange={handleFilterChange}
                      placeholder="Patient name / mobile / order no"
                      className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Order ID (exact) */}
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Order ID</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="orderId"
                    value={filters.orderId}
                    onChange={handleFilterChange}
                    placeholder="e.g. 2747"
                    className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Bill ID */}
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Bill ID</label>
                  <input
                    type="text"
                    name="billId"
                    value={filters.billId}
                    onChange={handleFilterChange}
                    placeholder="PAY- / razorpay id"
                    className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* ✅ Diagnostic Center dropdown */}
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Diagnostic Center</label>
                  <select
                    name="diagnosticCenterId"
                    value={filters.diagnosticCenterId}
                    onChange={handleFilterChange}
                    disabled={isLoadingCenters}
                    className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Diagnostic Centers</option>
                    {(user?.role === "superadmin" ? diagnosticCenters : diagnosticCenters).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Payment Method */}
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Payment Method</label>
                  <select
                    name="paymentMethod"
                    value={filters.paymentMethod}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Methods</option>
                    <option value="CASH">Cash</option>
                    <option value="CARD">Card</option>
                    <option value="UPI">UPI</option>
                    <option value="NETBANKING">Net Banking</option>
                    <option value="WALLET">Wallet</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>

                {/* Date Range */}
                <div className="col-span-12 md:col-span-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Date Range (From → To)
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <input
                        type="date"
                        value={filters.startDate || ""}
                        max={filters.endDate || undefined}
                        onChange={(e) => onDatePickChange("start", e.target.value)}
                        className="w-full px-3 py-1.5 text-xs border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 border-gray-300 cursor-pointer"
                      />
                    </div>

                    <span className="self-center text-xs text-gray-500">to</span>

                    <div className="flex-1">
                      <input
                        type="date"
                        value={filters.endDate || ""}
                        min={filters.startDate || undefined}
                        onChange={(e) => onDatePickChange("end", e.target.value)}
                        className="w-full px-3 py-1.5 text-xs border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 border-gray-300 cursor-pointer"
                      />
                    </div>
                  </div>
                  <p className="mt-1 text-[10px] text-gray-400">Click to pick a date</p>
                </div>

                {/* Rows */}
                <div className="col-span-12 md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Rows</label>
                  <select
                    name="limit"
                    value={filters.limit}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        limit: Number(e.target.value),
                        page: 1,
                      }))
                    }
                    className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
            </form>
          </div>

          {/* Loading */}
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-xs text-gray-600">Loading payments...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-white border border-red-200 rounded-lg p-4 text-xs text-red-700">
              {error}
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          S.No
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Patient & Order
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Diagnostic Center
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Payment Details
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                      {bills.length > 0 ? (
                        bills.map((bill, index) => (
                          <tr key={bill.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2.5 text-center text-gray-500 font-medium">
                              {getSerialNumber(index)}
                            </td>

                            <td className="px-3 py-2.5">
                              <div>
                                <div className="font-medium text-gray-900">
                                  {bill.patient?.fullName || "—"}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {bill.patient?.contactNo || "—"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Order: {bill.order?.id || bill.order?.id || "—"}
                                </div>
                              </div>
                            </td>

                            {/* ✅ Diagnostic Center column */}
                            <td className="px-3 py-2.5">
                              <div className="flex items-start gap-2">
                                <span className="mt-0.5 text-gray-500">
                                  <Building2 className="w-3.5 h-3.5" />
                                </span>
                                <div>
                                  <div className="text-xs font-medium text-gray-900">
                                    {bill.diagnosticCenter?.name || bill.order?.diagnosticCenter?.name || "—"}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    DC ID: {bill.diagnosticCenter?.id ?? bill.diagnosticCenterId ?? bill.order?.diagnosticCenter?.id ?? bill.order?.diagnosticCenterId ?? "—"}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs">{getPaymentMethodIcon(bill.paymentMethod)}</span>
                                <span className="text-xs font-medium text-gray-900">
                                  {bill.paymentMethod?.replace(/_/g, " ") || "—"}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                Bill ID: {bill.invoiceNumber || "—"}
                              </div>
                            </td>

                            <td className="px-3 py-2.5">
                              <div className="text-xs text-gray-900">{formatDate(bill.paymentDate)}</div>
                              <div className="text-xs text-gray-500">{formatDateTime(bill.createdAt)}</div>
                            </td>

                            <td className="px-3 py-2.5">
                              <span
                                className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getPaymentStatusColor(
                                  bill.paymentStatus
                                )}`}
                              >
                                {getPaymentStatusText(bill.paymentStatus)}
                              </span>
                            </td>

                            <td className="px-3 py-2.5">
                              <div className="font-medium text-gray-900">{formatCurrency(bill.amount)}</div>
                              {bill.refundAmount ? (
                                <div className="text-xs text-red-600">
                                  Refund: {formatCurrency(bill.refundAmount)}
                                </div>
                              ) : null}
                            </td>

                            <td className="px-3 py-2.5">
                              <div className="flex flex-wrap items-center gap-1">
                                <button
                                  onClick={() => navigate(`/orders/${bill.order?.id}`)}
                                  className="px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded text-xs font-medium flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" /> View
                                </button>

                                <button
                                  type="button"
                                  onClick={() => sendInvoiceWhatsApp(bill)}
                                  className="px-2 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded text-xs font-medium flex items-center gap-1"
                                >
                                  <MessageCircle className="w-3 h-3" /> WhatsApp
                                </button>

                                {bill.invoiceUrl ? (
                                  <a
                                    href={bill.invoiceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2 py-1 bg-green-50 text-green-700 hover:bg-green-100 rounded text-xs font-medium flex items-center gap-1"
                                  >
                                    <Download className="w-3 h-3" /> Invoice
                                  </a>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => generateInvoiceManually(bill)}
                                    disabled={generatingInvoiceId === bill.id}
                                    title="Invoice PDF missing — click to generate"
                                    className="px-2 py-1 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-60 disabled:cursor-not-allowed rounded text-xs font-medium flex items-center gap-1"
                                  >
                                    {generatingInvoiceId === bill.id ? (
                                      <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Generating…
                                      </>
                                    ) : (
                                      <>
                                        <FileText className="w-3 h-3" />
                                        Generate Invoice
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="px-3 py-8 text-center">
                            <div className="text-gray-400">
                              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p className="text-xs">No payment records found</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-600">
                    Showing{" "}
                    <span className="font-medium">{(filters.page - 1) * filters.limit + 1}</span> to{" "}
                    <span className="font-medium">{Math.min(filters.page * filters.limit, pagination.total)}</span> of{" "}
                    <span className="font-medium">{pagination.total}</span> payments
                  </div>

                  <div className="flex items-center gap-1 flex-wrap justify-end">
                    <button
                      onClick={() => handlePageChange(filters.page - 1)}
                      disabled={filters.page === 1}
                      className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>

                    {pageButtons.map((p, i) =>
                      p === "..." ? (
                        <span key={`dots-${i}`} className="px-2 text-gray-400 select-none">
                          ...
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => handlePageChange(p)}
                          className={`px-2.5 py-1 border rounded text-xs ${
                            filters.page === p
                              ? "bg-blue-600 text-white border-blue-600"
                              : "border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {p}
                        </button>
                      )
                    )}

                    <button
                      onClick={() => handlePageChange(filters.page + 1)}
                      disabled={filters.page === pagination.pages}
                      className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default BillsList;
